import { getStreamingUrl } from './musicService';
import api from './api';
import { logger } from '../utils/logger';
import { getSongCoverImageSrc } from '../utils/coverImages';
import { resolveApiBaseUrl } from '../utils/apiConfig';
import { extractSongLyrics, getSongLearningMetadata } from '../utils/songLearningMetadata';

const OFFLINE_AUDIO_CACHE = 'eduvibz-offline-audio-v1';
const OFFLINE_AUDIO_MANIFEST = 'eduvibz.offlineAudioManifest.v1';
const MIN_FREE_SPACE_AFTER_SAVE_BYTES = 25 * 1024 * 1024;

const isBrowser = () => typeof window !== 'undefined';
const getStorage = () => (isBrowser() ? window.localStorage : null);
const getCacheStorage = () => (typeof caches !== 'undefined' ? caches : null);

const manifestRequest = (songId) => `/offline-audio/${songId}`;

const isLikelyAudioResponse = (response, blob) => {
  const contentType = (blob?.type || response.headers?.get?.('content-type') || '').toLowerCase();
  if (!contentType) return true;
  if (contentType.startsWith('audio/')) return true;
  if (contentType.includes('octet-stream')) return true;
  return false;
};

const readManifest = () => {
  try {
    return JSON.parse(getStorage()?.getItem(OFFLINE_AUDIO_MANIFEST) || '{}');
  } catch {
    return {};
  }
};

const writeManifest = (manifest) => {
  try {
    getStorage()?.setItem(OFFLINE_AUDIO_MANIFEST, JSON.stringify(manifest));
  } catch {
    // Offline library metadata is best-effort; storage quotas can vary by browser.
  }
};

const blobToDataUrl = (blob) => new Promise((resolve) => {
  if (!blob?.size || typeof FileReader === 'undefined') {
    resolve('');
    return;
  }

  const reader = new FileReader();
  reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
  reader.onerror = () => resolve('');
  reader.readAsDataURL(blob);
});

const getApiBaseUrl = () => resolveApiBaseUrl({
  envUrl: import.meta.env.VITE_API_URL,
  isDev: import.meta.env.DEV
});

const isRemoteCoverImage = (coverImageSrc) => /^https?:\/\//i.test(coverImageSrc);

const getRemoteCoverHost = (coverImageSrc) => {
  try {
    return new URL(coverImageSrc).hostname.toLowerCase();
  } catch {
    return '';
  }
};

const shouldUseProxyFirst = (coverImageSrc) => (
  getRemoteCoverHost(coverImageSrc) === 'assets.eduvibz.com'
);

const fetchProxiedCoverImageBlob = async (coverImageSrc) => {
  const proxyUrl = `${getApiBaseUrl()}/assets/cover-image?url=${encodeURIComponent(coverImageSrc)}`;
  const proxyResponse = await fetch(proxyUrl, {
    mode: 'cors',
    credentials: 'omit'
  });

  if (!proxyResponse.ok) {
    throw new Error(`Cover image proxy failed with status ${proxyResponse.status}`);
  }

  return proxyResponse.blob();
};

const fetchDirectCoverImageBlob = async (coverImageSrc) => {
  const directResponse = await fetch(coverImageSrc, { mode: 'cors' });
  if (!directResponse.ok) {
    throw new Error(`Cover image fetch failed with status ${directResponse.status}`);
  }
  return directResponse.blob();
};

const fetchCoverImageBlob = async (coverImageSrc) => {
  if (isRemoteCoverImage(coverImageSrc) && shouldUseProxyFirst(coverImageSrc)) {
    try {
      return await fetchProxiedCoverImageBlob(coverImageSrc);
    } catch {
      // Fall through to the direct fetch so external image hosts still get one last chance.
    }
  }

  try {
    return await fetchDirectCoverImageBlob(coverImageSrc);
  } catch (error) {
    if (!isRemoteCoverImage(coverImageSrc)) {
      throw error;
    }
  }

  return fetchProxiedCoverImageBlob(coverImageSrc);
};

const cacheCoverImage = async (song) => {
  const coverImageSrc = getSongCoverImageSrc(song);
  if (!coverImageSrc || coverImageSrc.startsWith('data:')) return coverImageSrc || '';

  try {
    const blob = await fetchCoverImageBlob(coverImageSrc);
    const dataUrl = await blobToDataUrl(blob);
    return dataUrl || coverImageSrc;
  } catch (error) {
    logger.warn('Failed to cache offline cover image', {
      songId: song?._id,
      error: error.message
    });
    return coverImageSrc;
  }
};

export const isOfflineAudioSupported = () => Boolean(
  getCacheStorage()
  && typeof fetch === 'function'
  && typeof Response !== 'undefined'
  && typeof URL !== 'undefined'
  && typeof URL.createObjectURL === 'function'
);

export const getOfflineAudioManifest = () => readManifest();

export const hasOfflineAudioManifestEntry = (songId) => Boolean(readManifest()[songId]);

const hasSavedLearningMetadata = (entry = {}) => Boolean(
  entry.lyrics
  || entry.hasTimedLyrics
  || entry.timedLyrics?.length
  || entry.keyConcepts?.length
  || entry.studyNotes?.length
  || entry.flashcards?.length
);

export const refreshOfflineAudioMetadata = async (songId) => {
  if (!songId || !hasOfflineAudioManifestEntry(songId)) return null;

  const manifest = readManifest();
  const existing = manifest[songId];

  try {
    const response = await api.get(`/music/song/${songId}`);
    const song = response.data;
    if (!song?._id) return existing;

    const learningMetadata = getSongLearningMetadata(song);
    const lyrics = extractSongLyrics(song);
    const coverImageSrc = getSongCoverImageSrc(song) || existing.coverImageSrc || '';
    const coverImageDataUrl = existing.coverImageDataUrl || await cacheCoverImage(song);

    manifest[songId] = {
      ...existing,
      title: song.title || existing.title,
      artist: song.artist || existing.artist,
      chapterId: song.chapter?._id || existing.chapterId,
      chapterName: song.chapter?.name || song.album?.title || existing.chapterName,
      subjectName: song.chapter?.subject?.name || song.subject?.name || existing.subjectName,
      coverImageSrc,
      coverImageDataUrl,
      lyrics: lyrics || existing.lyrics || '',
      timedLyrics: learningMetadata.timedLyrics,
      hasTimedLyrics: learningMetadata.hasTimedLyrics,
      lyricOffsetSeconds: learningMetadata.lyricOffsetSeconds,
      keyConcepts: learningMetadata.concepts,
      studyNotes: learningMetadata.studyNotes,
      flashcards: learningMetadata.flashcards,
      progress: learningMetadata.progress,
      metadataRefreshedAt: new Date().toISOString()
    };

    writeManifest(manifest);
    return manifest[songId];
  } catch (error) {
    logger.warn('Failed to refresh offline song metadata', {
      songId,
      error: error.message
    });
    return existing;
  }
};

export const isSongAvailableOffline = async (songId) => {
  if (!songId || !hasOfflineAudioManifestEntry(songId) || !getCacheStorage()) {
    return false;
  }

  const cache = await caches.open(OFFLINE_AUDIO_CACHE);
  const response = await cache.match(manifestRequest(songId));
  if (response) return true;

  const manifest = readManifest();
  delete manifest[songId];
  writeManifest(manifest);
  return false;
};

export const getOfflineAudioUsage = () => Object.values(readManifest()).reduce((total, entry) => (
  total + (Number(entry?.sizeBytes) || 0)
), 0);

export const estimateOfflineStorage = async () => {
  if (!navigator.storage?.estimate) {
    return {
      supported: false,
      usage: getOfflineAudioUsage(),
      quota: null,
      available: null
    };
  }

  const estimate = await navigator.storage.estimate();
  const usage = Number(estimate.usage) || 0;
  const quota = Number(estimate.quota) || 0;

  return {
    supported: true,
    usage,
    quota,
    available: quota ? Math.max(0, quota - usage) : null,
    offlineAudioUsage: getOfflineAudioUsage()
  };
};

export const hasEnoughOfflineStorage = async (requiredBytes = 0) => {
  const estimate = await estimateOfflineStorage();
  if (!estimate.supported || estimate.available === null) return true;
  return estimate.available - requiredBytes >= MIN_FREE_SPACE_AFTER_SAVE_BYTES;
};

export const removeOfflineAudio = async (songId) => {
  if (!songId || !getCacheStorage()) return false;

  const cache = await caches.open(OFFLINE_AUDIO_CACHE);
  await cache.delete(manifestRequest(songId));

  const manifest = readManifest();
  delete manifest[songId];
  writeManifest(manifest);
  return true;
};

export const clearOfflineAudio = async () => {
  const manifest = readManifest();
  const entries = Object.keys(manifest);

  if (getCacheStorage()) {
    const cache = await caches.open(OFFLINE_AUDIO_CACHE);
    await Promise.all(entries.map((songId) => cache.delete(manifestRequest(songId))));
  }

  writeManifest({});
  return entries.length;
};

export const saveSongForOffline = async (song) => {
  if (!song?._id) {
    throw new Error('A song is required for offline saving');
  }
  if (!isOfflineAudioSupported()) {
    throw new Error('Offline audio is not supported in this browser');
  }

  const { url } = await getStreamingUrl(song._id);
  const response = await fetch(url, { mode: 'cors' });

  if (!response.ok) {
    throw new Error(`Audio download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  if (!blob?.size) {
    throw new Error('Downloaded audio was empty');
  }
  if (!isLikelyAudioResponse(response, blob)) {
    throw new Error('Downloaded file was not a playable audio track');
  }
  if (!(await hasEnoughOfflineStorage(blob.size))) {
    throw new Error('Not enough device storage to save this track offline');
  }

  const cache = await caches.open(OFFLINE_AUDIO_CACHE);
  await cache.put(manifestRequest(song._id), new Response(blob, {
    headers: {
      'Content-Type': blob.type || song.assetMetadata?.audio?.contentType || 'audio/mpeg',
      'X-EduVibz-Offlined-At': new Date().toISOString()
    }
  }));

  const manifest = readManifest();
  const coverImageSrc = getSongCoverImageSrc(song);
  const coverImageDataUrl = await cacheCoverImage(song);
  const learningMetadata = getSongLearningMetadata(song);
  const lyrics = extractSongLyrics(song);

  manifest[song._id] = {
    songId: song._id,
    title: song.title,
    artist: song.artist,
    chapterId: song.chapter?._id,
    chapterName: song.chapter?.name || song.album?.title,
    subjectName: song.chapter?.subject?.name || song.subject?.name,
    coverImageSrc,
    coverImageDataUrl,
    lyrics,
    timedLyrics: learningMetadata.timedLyrics,
    hasTimedLyrics: learningMetadata.hasTimedLyrics,
    lyricOffsetSeconds: learningMetadata.lyricOffsetSeconds,
    keyConcepts: learningMetadata.concepts,
    studyNotes: learningMetadata.studyNotes,
    flashcards: learningMetadata.flashcards,
    progress: learningMetadata.progress,
    sizeBytes: blob.size,
    contentType: blob.type || song.assetMetadata?.audio?.contentType || 'audio/mpeg',
    savedAt: new Date().toISOString()
  };
  writeManifest(manifest);

  return manifest[song._id];
};

export const shouldRefreshOfflineAudioMetadata = (entry = {}) => !hasSavedLearningMetadata(entry);

export const getOfflineAudioUrl = async (songId) => {
  if (!songId || !hasOfflineAudioManifestEntry(songId) || !getCacheStorage()) return null;

  try {
    const cache = await caches.open(OFFLINE_AUDIO_CACHE);
    const response = await cache.match(manifestRequest(songId));
    if (!response) {
      const manifest = readManifest();
      delete manifest[songId];
      writeManifest(manifest);
      return null;
    }

    const blob = await response.blob();
    if (!blob?.size) {
      const manifest = readManifest();
      delete manifest[songId];
      writeManifest(manifest);
      return null;
    }

    return URL.createObjectURL(blob);
  } catch (error) {
    logger.warn('Failed to load offline audio', {
      songId,
      error: error.message
    });
    return null;
  }
};
